import { Module, VuexModule, Mutation, getModule } from 'vuex-module-decorators';
import _ from 'lodash'
import Debug from 'debug';
import { ok, err, Result } from '@xlearn/common'
import store from '@/store';
import { createUpserter, mergeRightToLeft } from '@/app/shared/utils'
import { injectable } from 'inversify';
import { RemoteQueryPhase } from './interaction-query';

export const InteractionStoreModuleSymbol = Symbol.for('InteractionStoreModule');

const stateUpserter = createUpserter(defaultInteractionState);
const resultUpserter = createUpserter(defaultInteractionResult)

const debug = Debug('interaction-store-module')

interface InteractionState<TState> {
  id: string
  type: string
  state: TState
}

interface InteractionResult {
  id: string
  progress: number
  status: 'not-attempted' | 'incomplete' | 'completed'
}

interface UpdateStatePayload {
  id: string
  type: string
  state: unknown
}

interface UpdateResultPayload {
  id: string
  progress: number
  status: 'incomplete' | 'completed'
}

type FindRemoteStateError = 'not-loaded' | 'not-found'
type FindLocalStateError = 'not-found'
type FindStateError = 'not-found'

@injectable()
@Module({
  namespaced: true,
  name: 'interaction',
  store,
  dynamic: true
})
export class InteractionStateStoreModule extends VuexModule {

  private remoteStatesQueryPhase: RemoteQueryPhase = 'none';
  private remoteResultsQueryPhase: RemoteQueryPhase = 'none';

  private localStates: InteractionState<unknown>[] = []
  private remoteStates: InteractionState<unknown>[] = []

  private localResults: InteractionResult[] = []
  private remoteResults: InteractionResult[] = []

  @Mutation
  public updateState(payload: UpdateStatePayload) {
    debug('updateState', payload)

    this.localStates = stateUpserter(this.localStates, payload.id, () => ({
      id: payload.id,
      type: payload.type,
      state: payload.state
    }))

    debug('states after updateState', this.localStates)
  }

  get remoteStatePhase() {
    return this.remoteStatesQueryPhase
  }

  get remoteResultPhase() {
    return this.remoteResultsQueryPhase
  }

  @Mutation
  public updateResult(payload: UpdateResultPayload) {
    debug('updateResult', payload)

    this.localResults = resultUpserter(this.localResults, payload.id, () => ({
      id: payload.id,
      progress: payload.progress,
      status: payload.status
    }))

    debug('results after updateResult', this.localResults)
  }

  @Mutation
  public startStatesRemoteQuerying() {
    this.remoteStatesQueryPhase = 'loading';
    this.remoteStates = []
  }

  @Mutation
  public finishStatesRemoteQuerying(states: InteractionState<unknown>[]) {
    this.remoteStatesQueryPhase = 'loaded'
    this.remoteStates = states
  }

  @Mutation
  public startResultsRemoteQuerying() {
    this.remoteResultsQueryPhase = 'loading'
  }

  @Mutation
  public finishResultsRemoteQuerying(results: InteractionResult[]) {
    this.remoteResultsQueryPhase = 'loaded'
    this.remoteResults = results
  }

  get states() {
    debug('merging interaction states; uses local if found in local', this.localStates, this.remoteStates)
    const merged = mergeRightToLeft(this.localStates, this.remoteStates, merge)
    debug('merged', merged)

    return merged

    function merge(local: InteractionState<unknown>, _remote: InteractionState<unknown>) {
      return local
    }
  }

  get findState() {
    const self = this

    return function <TState>(id: string): Result<InteractionState<TState>, FindStateError> {
      const found = self.states.find(x => x.id === id)
      if (!found) {
        return err('not-found' as FindStateError)
      }

      return ok(found as InteractionState<TState>)
    }
  }

  get findRemoteState() {
    const self = this

    return function <TState>(id: string): Result<InteractionState<TState>, FindRemoteStateError> {
      if (self.remoteStatePhase !== 'loaded') {
        return err('not-loaded' as FindRemoteStateError)
      }

      const found = self.remoteStates.find(x => x.id === id)
      if (!found) {
        return err('not-found' as FindRemoteStateError)
      }

      return ok(found as InteractionState<TState>)
    }
  }

  get findLocalState() {
    const self = this

    return function <TState>(id: string): Result<InteractionState<TState>, FindLocalStateError> {
      const found = self.localStates.find(x => x.id === id)
      if (!found) {
        return err('not-found' as FindLocalStateError)
      }

      return ok({
        id: found.id,
        type: found.type,
        state: found.state as TState
      })
    }
  }

  get results() {
    debug('merging interaction results; uses local if found in local', this.localResults, this.remoteResults)
    const merged = mergeRightToLeft(this.localResults, this.remoteResults, merge)
    debug('merged', merged)

    return merged

    function merge(_remote: InteractionResult, local: InteractionResult) {
      return local
    }
  }
}

export default getModule(InteractionStateStoreModule);

function defaultInteractionState(): InteractionState<unknown> {
  return {
    id: '',
    type: '',
    state: {}
  }
}

function defaultInteractionResult(): InteractionResult {
  return {
    id: '',
    progress: 0,
    status: 'not-attempted'
  }
}
